import { useState, useRef } from 'react';
import { useArrowKeyHandler } from '../../hooks/use-arrow-key-handler.js';
import { accept } from '../../utils/event.js';
import { clamp as clamp$1 } from '../../utils/math.js';
import { PressedKeys } from '../../utils/pressed-keys.js';
import { scale, clamp, deltaSnapNonZero, equal, vec } from '../../utils/vector.js';

function useSlider2DKeyboard({ value, min, max, step, bigStep, disabled, onChange, rootRef, }) {
    const [focusableInput, setFocusableInput] = useState('initial');
    function focusAxis(axis) {
        setFocusableInput((prev) => {
            if (prev === axis)
                return prev;
            // When moving from 'initial' to 'x', the X input is already focused —
            // no DOM focus change needed, just update the state.
            if (prev === 'initial' && axis === 'x')
                return axis;
            // Imperatively move focus to the input for the new axis. We schedule this
            // in a microtask so the DOM has updated (tabIndex swap) before we focus.
            queueMicrotask(() => {
                const input = rootRef.current?.querySelector(`input[data-axis="${axis}"]`);
                input?.focus();
            });
            return axis;
        });
    }
    function onFocus(e) {
        const axis = e.target.getAttribute('data-axis');
        setFocusableInput((prev) => {
            // Preserve 'initial' state when the X input receives focus for the first time
            if (prev === 'initial' && axis === 'x')
                return prev;
            return axis;
        });
    }
    // Attached to the root so we only see one blur when focus leaves the slider
    // entirely. Intra-slider moves (e.g. VO swipe between X and Y) don't bubble
    // out, so no need to check relatedTarget against siblings.
    function onBlur(e) {
        const next = e.relatedTarget;
        if (next && rootRef.current?.contains(next))
            return;
        setFocusableInput('initial');
    }
    // iPad VoiceOver's "swipe up/down to adjust" gesture drives the native range
    // directly, firing a change event on the focused input without any
    // corresponding keydown. Keyboard arrows go through `onKeyDown` + `accept`,
    // which preventDefaults the native value change, so this handler only sees
    // assistive-tech-initiated changes.
    function onInputChange(e) {
        if (disabled)
            return;
        const axis = e.target.getAttribute('data-axis');
        const raw = Number(e.target.value);
        if (!Number.isFinite(raw))
            return;
        const clamped = clamp$1(raw, min[axis], max[axis]);
        const newValue = axis === 'x' ? vec(clamped, value.y) : vec(value.x, clamped);
        if (equal(newValue, value))
            return;
        focusAxis(axis);
        onChange(newValue, { event: e, commit: true });
    }
    const lastEvent = useRef(null);
    const arrowHandlers = useArrowKeyHandler({
        onKeyDown(e) {
            if (disabled)
                return false;
            lastEvent.current = e;
        },
        onDelta(delta) {
            const mods = PressedKeys.modifiers();
            const s = mods.shiftKey ? bigStep : step;
            const scaled = scale(delta, s);
            const newValue = clamp(deltaSnapNonZero(value, scaled, step), min, max);
            if (!equal(newValue, value)) {
                // Both axes can change in one update (e.g. diagonal arrow combos), but we can only
                // focus one — prefer X.
                const axis = delta.x !== 0 ? 'x' : delta.y !== 0 ? 'y' : null;
                if (axis)
                    focusAxis(axis);
                onChange(newValue, { event: lastEvent.current, commit: true });
            }
        },
    });
    function onKeyDown(e) {
        if (disabled)
            return;
        let newValue = null;
        let axis = null;
        switch (e.code) {
            case 'PageUp':
                newValue = vec(value.x, min.y);
                axis = 'y';
                break;
            case 'PageDown':
                newValue = vec(value.x, max.y);
                axis = 'y';
                break;
            case 'Home':
                newValue = vec(min.x, value.y);
                axis = 'x';
                break;
            case 'F4':
            case 'End':
                newValue = vec(max.x, value.y);
                axis = 'x';
                break;
            default:
                arrowHandlers.onKeyDown(e);
                return;
        }
        if (axis)
            focusAxis(axis);
        if (newValue) {
            accept(e);
            if (!equal(newValue, value)) {
                onChange(newValue, { event: e, commit: true });
            }
            return;
        }
    }
    const onKeyUp = arrowHandlers.onKeyUp;
    return { focusableInput, onChange: onInputChange, onKeyDown, onKeyUp, onFocus, onBlur };
}

export { useSlider2DKeyboard };
//# sourceMappingURL=slider-2d-primitive.hooks.js.map
